import { Badge, Price, Text } from "@/components/ui";
import { cn } from "@/lib/cn";
import { sanitizeMoney } from "@/lib/pricing";
import type { ItemOffer } from "@/types";

type ItemPriceBadgeProps = {
  itemOffer: ItemOffer;
  minimumOfferPrice: number | null;
  className?: string;
};

/**
 * Dark price pill over the item photo in the detail modal.
 *
 * `secondary` is the store PRICE role — it paints the number, not the chip.
 * The chip is the opaque `inverse` Badge (`gray-scale-900`, structural dark, not a palette role) so a
 * pale price color still reads and a dark one doesn't melt into a filled slab.
 * "a partir de" / strikethrough use `on-media`: they sit on that dark chip, not
 * on a merchant color.
 */
export function ItemPriceBadge({
  itemOffer,
  minimumOfferPrice,
  className,
}: ItemPriceBadgeProps) {
  const value = itemOffer.price.value;
  const originalValue = itemOffer.price.originalValue;

  const hasRealDiscount = originalValue != null && originalValue > value;

  const originalFloor = Math.max(
    0,
    sanitizeMoney(minimumOfferPrice) - sanitizeMoney(value) + sanitizeMoney(originalValue),
  );

  const currentPrice = sanitizeMoney(minimumOfferPrice ?? value);

  return (
    <Badge variant="solid" tone="inverse" opaque size="md" className={cn("h-20 flex-col justify-center gap-0", className)}>
      <span className="flex flex-col items-center justify-center px-3.5">
        {!value ? (
          <Text as="small" size="lg" weight="semibold" tone="on-media">
            a partir de
          </Text>
        ) : null}
        {hasRealDiscount && <Price strike value={originalFloor} size="xl" weight="semibold" tone="on-media" />}
        <Price value={currentPrice} size="2xl" weight="bold" tone="secondary" className="brightness-135" />
      </span>
    </Badge>
  );
}
